import { useEffect, useRef } from 'react';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../firebase';

const GOOGLE_CLIENT_ID = '1025490770112-e7ihu93m5c2q4ev6auibg3q45hdban6r.apps.googleusercontent.com';

/**
 * Shows the Google One Tap popup in the top-right corner for non-logged-in users.
 * When the user signs in via One Tap, it converts the credential to a Firebase auth session.
 */
export function useGoogleOneTap(user, authLoading) {
  const initialized = useRef(false);

  useEffect(() => {
    // Only show One Tap if: not logged in (anonymous counts as not logged in),
    // auth finished loading
    if (authLoading || (user && !user.isAnonymous) || initialized.current) return;

    const showOneTap = () => {
      if (initialized.current) return;
      if (!window.google?.accounts?.id) return;

      initialized.current = true;

      console.log('One Tap check:', {
        authLoading,
        hasUser: !!user,
        isAnonymous: user?.isAnonymous,
        sdkLoaded: !!window.google?.accounts?.id,
      });

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            const credential = GoogleAuthProvider.credential(response.credential);
            await signInWithCredential(auth, credential);
          } catch (err) {
            console.error('One Tap sign-in error:', err);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        use_fedcm_for_prompt: true,
      });

      const promptTimeout = setTimeout(() => {
        console.log('One Tap: prompt() callback never fired (5s timeout)');
      }, 5000);

      window.google.accounts.id.prompt((notification) => {
        clearTimeout(promptTimeout);
        if (notification.isNotDisplayed()) {
          console.log('One Tap not displayed:', notification.getNotDisplayedReason());
        } else if (notification.isSkippedMoment()) {
          console.log('One Tap skipped:', notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          console.log('One Tap dismissed:', notification.getDismissedReason());
        }
      });
    };

    // SDK might not be loaded yet (async defer script) — poll until ready
    if (window.google?.accounts?.id) {
      showOneTap();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          showOneTap();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [user, authLoading]);

  // Reset when user logs out (or becomes anonymous) so One Tap can show again
  useEffect(() => {
    if ((!user || user.isAnonymous) && !authLoading && initialized.current) {
      initialized.current = false;
    }
  }, [user, authLoading]);
}
